import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type CarouselItem } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export default function LayoutTemplateBar({
  item,
  disabled = false,
  className,
  hint,
}: {
  item: CarouselItem;
  disabled?: boolean;
  className?: string;
  hint?: string;
}) {
  const qc = useQueryClient();
  const listQ = useQuery({
    queryKey: ["carousel-layout-templates"],
    queryFn: () => api.carouselLayoutTemplates(),
  });
  const items = listQ.data?.items || [];
  const [selectedId, setSelectedId] = useState(() => item.layout_template_id || "");
  const [saveName, setSaveName] = useState("");
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [error, setError] = useState("");

  const hasLayout = useMemo(
    () => (item.slides || []).some((row) => (row.layout?.boxes || []).length > 0),
    [item.slides],
  );

  const selected = items.find((row) => row.id === selectedId);

  function bumpCarousel(next: CarouselItem) {
    qc.setQueryData(["carousel", item.id], next);
    qc.invalidateQueries({ queryKey: ["carousel", item.id] });
  }

  const applyM = useMutation({
    mutationFn: () => api.carouselApplyLayoutTemplate(item.id, selectedId),
    onSuccess: (next) => {
      setError("");
      bumpCarousel(next);
    },
    onError: (err: Error) => setError(err.message || "Nie udało się zastosować template'u"),
  });

  const saveM = useMutation({
    mutationFn: (payload: { name: string; overwrite_id?: string }) =>
      api.carouselSaveLayoutTemplate(item.id, payload),
    onSuccess: (res) => {
      setError("");
      setSaveName("");
      if (res.item?.id) setSelectedId(res.item.id);
      if (res.carousel) bumpCarousel(res.carousel);
      qc.invalidateQueries({ queryKey: ["carousel-layout-templates"] });
    },
    onError: (err: Error) => setError(err.message || "Nie udało się zapisać template'u"),
  });

  const renameM = useMutation({
    mutationFn: () => api.carouselRenameLayoutTemplate(selectedId, renameValue.trim()),
    onSuccess: () => {
      setError("");
      setRenameOpen(false);
      qc.invalidateQueries({ queryKey: ["carousel-layout-templates"] });
    },
    onError: (err: Error) => setError(err.message || "Nie udało się zmienić nazwy"),
  });

  const deleteM = useMutation({
    mutationFn: () => api.carouselDeleteLayoutTemplate(selectedId),
    onSuccess: () => {
      setError("");
      setSelectedId("");
      qc.invalidateQueries({ queryKey: ["carousel-layout-templates"] });
    },
    onError: (err: Error) => setError(err.message || "Nie udało się usunąć template'u"),
  });

  const busy =
    disabled ||
    applyM.isPending ||
    saveM.isPending ||
    renameM.isPending ||
    deleteM.isPending;

  return (
    <div className={cn("space-y-2 rounded-md border border-input bg-card/40 p-3", className)}>
      <div className="space-y-1">
        <p className="text-xs font-medium text-foreground">Szablon układu (first / mid / last)</p>
        <p className="text-[11px] text-muted-foreground">
          {hint ||
            "Zapisuje pozycje, fonty, kolory i tło slajdów 2+. Zdjęcie pierwszego slajdu zostaje. Zastosowanie nadpisuje układ aktywnego slajdu."}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <select
          className="min-w-[12rem] flex-1 rounded-md border border-input bg-transparent px-2 py-1.5 text-sm"
          value={selectedId}
          disabled={busy || listQ.isLoading}
          onChange={(e) => setSelectedId(e.target.value)}
        >
          <option value="">Wybierz template…</option>
          {items.map((row) => (
            <option key={row.id} value={row.id}>
              {row.name}
              {row.roles?.length ? ` (${row.roles.join(", ")})` : ""}
            </option>
          ))}
        </select>
        <Button
          size="sm"
          disabled={busy || !selectedId}
          onClick={() => applyM.mutate()}
        >
          {applyM.isPending ? "Stosuję…" : "Zastosuj template"}
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={busy || !selectedId}
          onClick={() => {
            setRenameValue(selected?.name || "");
            setRenameOpen((v) => !v);
          }}
        >
          Zmień nazwę
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={busy || !selectedId}
          onClick={() => {
            if (!selectedId) return;
            if (!window.confirm(`Usunąć template „${selected?.name || selectedId}”?`)) return;
            deleteM.mutate();
          }}
        >
          Usuń
        </Button>
      </div>
      {renameOpen && selectedId ? (
        <div className="flex flex-wrap items-center gap-2">
          <input
            className="min-w-[12rem] flex-1 rounded-md border border-input bg-transparent px-2 py-1.5 text-sm"
            value={renameValue}
            maxLength={120}
            disabled={busy}
            onChange={(e) => setRenameValue(e.target.value)}
            placeholder="Nowa nazwa"
          />
          <Button
            size="sm"
            disabled={busy || !renameValue.trim()}
            onClick={() => renameM.mutate()}
          >
            Zapisz nazwę
          </Button>
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        <input
          className="min-w-[12rem] flex-1 rounded-md border border-input bg-transparent px-2 py-1.5 text-sm"
          value={saveName}
          maxLength={120}
          disabled={busy || !hasLayout}
          onChange={(e) => setSaveName(e.target.value)}
          placeholder={hasLayout ? "Nazwa nowego template'u" : "Najpierw złóż / ustaw layout"}
        />
        <Button
          size="sm"
          variant="secondary"
          disabled={busy || !hasLayout || !saveName.trim()}
          onClick={() => saveM.mutate({ name: saveName.trim() })}
        >
          Zapisz układ jako template
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={busy || !hasLayout || !selectedId}
          onClick={() => {
            if (!selectedId) return;
            const name = saveName.trim() || selected?.name || "";
            if (!name) {
              setError("Podaj nazwę albo wybierz template do nadpisania.");
              return;
            }
            if (!window.confirm(`Nadpisać template „${selected?.name || selectedId}” układem z tej karuzeli?`)) {
              return;
            }
            saveM.mutate({ name, overwrite_id: selectedId });
          }}
        >
          Nadpisz wybrany
        </Button>
      </div>
      {error ? <p className="text-[11px] text-destructive">{error}</p> : null}
    </div>
  );
}
